<template>
  <div class="disciplinary-actions-table" data-testid="disciplinary-actions-table-view">
    <disciplinary-actions-filters
      :visible.sync="visibleFiltersModal"
      :current-filters="activeFilters"
      :show-associate-filter="showAssociateColumn"
      @update:currentFilters="updateFilters"
    />

    <history-write-up
    :visible.sync="showHistoryWriteup"
    :associate-number.sync="writeUpAssociateNumber"
    :associate-profile-export="associateProfileExport"
    @export="$emit('export')"/>

    <table-layout :title="title" :show-header="showHeader">
      <template #controls-left>
        <slot name="actions" />
      </template>

      <template #controls-right>
        <table-associate-search
          v-if="showAssociateColumn"
          :filters="activeFilters"
          filter-key="associate_id"
          :is-associate-id="true"
          :multiply="false"
          :use-router="useRouter"
          data-testid="disciplinary-actions-associate-search"
          @update:filters="updateFilters"
        />

        <a-badge :count="countFilters" data-testid="disciplinary-actions-filters-count">
          <a-button icon="filter" @click="visibleFiltersModal = true" data-testid="disciplinary-actions-open-filters">
            Filters
          </a-button>
        </a-badge>

        <a-button :disabled="!canResetFilters" icon="undo" @click="resetFilters">Reset Filters</a-button>

        <a-button
          class="flex align-items-center justify-content-center"
          title="Refresh Page"
          @click="loadData"
          data-testid="disciplinary-actions-refresh"
        >
          <a-icon width="20" height="20" icon="solar:refresh-square-broken" />
        </a-button>
      </template>

      <template #filters>
        <active-table-filters
          :items="activeFilterTags"
          @edit="openFilterEditor"
          @remove="removeFilter"
          @reset="resetFilters"
        />
      </template>

      <a-table
        size="small"
        :columns="columns"
        :data-source="tableData"
        :loading="loading"
        :pagination="pagination"
        :row-key="record => record.id"
        :scroll="{ x: showAssociateColumn ? 1100 : 850, y: tableScrollHeight }"
        :row-class-name="getRowClassName"
        @change="handleTableChange"
        data-testid="disciplinary-actions-table"
      >
        <span slot="created_at" slot-scope="text, record">
          {{ formatDateTime(record.date) }}
        </span>

        <span slot="disciplinary_action" slot-scope="text, record">
          <div class="flex flex-column gap-1">
            <a-button
              v-if="record.type === 'write_up'"
              type="link"
              class="disciplinary-actions-table__action-link h-fit p-0"
              @click.stop="openWriteUp(record)"
            >
              {{ record.type_label }}
            </a-button>
            <b v-else>{{ record.type_label }}</b>

            <div v-if="getActionMetadata(record).length" class="flex flex-wrap gap-1 align-items-center">
              <a-tag v-for="item in getActionMetadata(record)" :key="item" class="m-0">{{ item }}</a-tag>
            </div>

            <div v-if="getRemovedShiftIds(record).length" class="flex flex-wrap gap-1">
              <a-button
                v-for="shiftId in getRemovedShiftIds(record)"
                :key="shiftId"
                type="link"
                class="h-fit p-0"
                @click.stop="openShift(shiftId, getRemovedShiftIds(record))"
              >
                Shift #{{ shiftId }}
              </a-button>
            </div>
          </div>
        </span>

        <span slot="associate" slot-scope="text, record">
          <a-button
            type="link"
            class="disciplinary-actions-table__person-link h-fit p-0"
            @click.stop="openProfile(record.associate)"
          >
            {{ getAssociateLabel(record.associate) }}
          </a-button>
        </span>

        <span slot="author" slot-scope="text, record">
          {{ getAuthorLabel(record.author) }}
        </span>

        <span slot="reason" slot-scope="text, record">
          <a-tooltip v-if="record.reason">
            <template slot="title">{{ record.reason }}</template>
            <div class="disciplinary-actions-table__reason">{{ record.reason }}</div>
          </a-tooltip>
          <span v-else>-</span>
        </span>
      </a-table>
    </table-layout>
  </div>
</template>

<script>
import { Badge, Button, notification, Table, Tag, Tooltip } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import tableMixin from '@/mixins/tableMixin'
import util from '@/util'
import TableAssociateSearch from '@/components/entities/table/table-associate-search.vue'
import HistoryWriteUp from '@/components/write-up/write-up-history-modal.vue'
import DisciplinaryActionsFilters from './disciplinary-actions-filters.vue'

const { mapActions: disciplinaryActions } = createNamespacedHelpers('disciplinary')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

const actionTypeLabels = Object.freeze({
  notification: 'Notification',
  block: 'Block',
  write_up: 'Write-Up',
  suspension: 'Suspension'
})

const emptyFilters = () => ({
  associate_id: undefined,
  start_date: undefined,
  end_date: undefined,
  types: []
})

export default {
  name: 'DisciplinaryActionsTable',
  components: {
    'a-table': Table,
    'a-button': Button,
    'a-badge': Badge,
    'a-tag': Tag,
    'a-tooltip': Tooltip,
    'table-associate-search': TableAssociateSearch,
    'history-write-up': HistoryWriteUp,
    'disciplinary-actions-filters': DisciplinaryActionsFilters
  },
  mixins: [tableMixin],
  props: {
    filters: {
      type: Object,
      default: () => ({})
    },
    associateId: {
      type: [Number, String],
      default: undefined
    },
    showAssociateColumn: {
      type: Boolean,
      default: true
    },
    useRouter: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Disciplinary Actions'
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    associateProfileExport: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      filterNew: emptyFilters(),
      tableData: [],
      loading: false,
      visibleFiltersModal: false,
      showHistoryWriteup: false,
      writeUpAssociateNumber: undefined,
      pagination: {
        pageSizeOptions: ['10', '50', '100', '200', '500'],
        pageSize: 50,
        current: 1,
        total: 0,
        showSizeChanger: true,
        showTotal: total => `Total: ${total}`
      }
    }
  },
  computed: {
    activeFilters() {
      return this.useRouter ? this.filters : this.filterNew
    },
    tableFilterStorage() {
      return {
        type: this.useRouter ? 'route' : 'local',
        filters: this.activeFilters
      }
    },
    columns() {
      const columns = [
        {
          title: 'Created At',
          key: 'created_at',
          width: 170,
          scopedSlots: { customRender: 'created_at' }
        },
        {
          title: 'Disciplinary Action',
          key: 'disciplinary_action',
          width: 300,
          scopedSlots: { customRender: 'disciplinary_action' }
        }
      ]

      if (this.showAssociateColumn) {
        columns.push({
          title: 'Associate',
          key: 'associate',
          width: 230,
          scopedSlots: { customRender: 'associate' }
        })
      }

      columns.push(
        {
          title: 'Author',
          key: 'author',
          width: 230,
          scopedSlots: { customRender: 'author' }
        },
        {
          title: 'Reason',
          key: 'reason',
          width: 260,
          scopedSlots: { customRender: 'reason' }
        }
      )

      return columns
    },
    tableResetFilters() {
      return emptyFilters()
    },
    tableResetQuery() {
      return {}
    },
    tableHiddenFilters() {
      return this.showAssociateColumn ? [] : ['associate_id']
    },
    tableFilterLabels() {
      return {
        associate_id: 'Associate',
        types: 'Disciplinary Action'
      }
    },
    tableFilterValueLabels() {
      return {
        associate_id: {
          [String(this.activeFilters.associate_id)]: `#${this.activeFilters.associate_id}`
        }
      }
    }
  },
  watch: {
    activeFilters: {
      handler(newValue, oldValue) {
        if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return

        this.pagination.current = 1
        this.loadData()
      },
      deep: true,
      immediate: true
    },
    associateId(newValue, oldValue) {
      if (oldValue === undefined || newValue === oldValue) return

      this.pagination.current = 1
      this.loadData()
    }
  },
  methods: {
    ...disciplinaryActions(['getDisciplinaryActions']),
    ...modalActions(['openModal']),

    getFilterValueLabel(key, value) {
      if (key === 'types' && Array.isArray(value)) {
        return value.map(type => actionTypeLabels[type] ?? this.humanize(type)).join(', ')
      }

      return tableMixin.methods.getFilterValueLabel.call(this, key, value)
    },
    updateFilters(filters) {
      if (this.useRouter) {
        const query = Object.entries(filters).reduce((result, [key, value]) => {
          const hasValue = Array.isArray(value)
            ? value.length > 0
            : value !== undefined && value !== null && value !== ''

          if (hasValue) result[key] = value
          return result
        }, {})

        this.$router.replace({ query }).catch(() => {})
        return
      }

      this.filterNew = { ...filters }
      this.$emit('update:filters', this.filterNew)
    },
    async loadData() {
      this.loading = true

      try {
        const data = await this.getDisciplinaryActions({
          ...this.activeFilters,
          associate_id: this.associateId ?? this.activeFilters.associate_id,
          page: this.pagination.current,
          per_page: this.pagination.pageSize
        })

        this.tableData = data.items ?? []
        this.pagination.total = data.count ?? 0
      } catch (error) {
        notification.error({
          message: 'Error',
          description: typeof error === 'string' ? error : error?.message
        })
      } finally {
        this.loading = false
      }
    },
    handleTableChange(pagination) {
      const changed = pagination.current !== this.pagination.current || pagination.pageSize !== this.pagination.pageSize

      if (!changed) return

      this.pagination = { ...this.pagination, ...pagination }
      this.loadData()
    },
    formatDateTime(value) {
      if (!value) return '-'

      const dateFormat = this.$store.state.applicationState.dateTimeFormat
      return util.convertDateTimeToCurrentTimeZoneWithFormat(value, dateFormat)
    },
    formatDate(value) {
      if (!value) return '-'

      const dateFormat = this.$store.state.applicationState.dateFormat || 'MM/DD/YYYY'
      return util.convertDateTimeToCurrentTimeZoneWithFormat(value, dateFormat)
    },
    getAssociateLabel(associate) {
      if (!associate) return '-'

      const name = util.combineAssociateName(associate.first_name, associate.last_name)
      return associate.associate_number ? `${name} ${associate.associate_number}` : name
    },
    getAuthorLabel(author) {
      if (!author || author.is_system) return 'System'

      return this.getAssociateLabel(author)
    },
    getActionMetadata(record) {
      const metadata = record.metadata ?? {}

      if (record.type === 'suspension') {
        const values = []
        const removedAmount = metadata.removed_amount ?? metadata.removed_shifts?.length

        if (metadata.amount !== undefined && removedAmount !== undefined) {
          values.push(`${removedAmount}/${metadata.amount} shifts removed`)
        } else if (removedAmount !== undefined) {
          values.push(`${removedAmount} shifts removed`)
        }

        if (metadata.status) values.push(this.humanize(metadata.status))
        return values
      }

      if (record.type === 'write_up') {
        return [metadata.disciplinary_action_type, metadata.status].filter(Boolean).map(this.humanize)
      }

      if (record.type === 'block') {
        const values = []

        if (metadata.indefinite) {
          values.push('Indefinite')
        } else if (metadata.start && metadata.end) {
          values.push(`${this.formatDate(metadata.start)} - ${this.formatDate(metadata.end)}`)
        } else if (metadata.start && metadata.days !== undefined) {
          values.push(`From ${this.formatDate(metadata.start)}, ${metadata.days} days`)
        } else if (metadata.start) {
          values.push(`From ${this.formatDate(metadata.start)}`)
        } else if (metadata.days !== undefined) {
          values.push(`${metadata.days} days`)
        }

        if (metadata.status) values.push(this.humanize(metadata.status))
        return values
      }

      return []
    },
    getRemovedShiftIds(record) {
      if (record.type !== 'suspension' || record.navigation?.target !== 'shift_penalty') return []

      return record.metadata?.removed_shifts ?? []
    },
    humanize(value) {
      if (!value) return '-'

      return String(value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, letter => letter.toUpperCase())
    },
    openProfile(associate) {
      if (!associate?.associate_number) return

      this.openModal({
        type: 'profile',
        data: {
          visible: true,
          associate_number: associate.associate_number,
          edit: () => {},
          open_tab: 'disciplinary',
          is_editing: false
        }
      })
    },
    openShift(shiftId, shiftIds) {
      this.openModal({
        type: 'shiftView',
        data: {
          visible: true,
          edit: () => this.loadData(),
          shift_id: Number(shiftId),
          shifts_list: shiftIds.map(Number)
        }
      })
    },
    openWriteUp(record) {
      this.writeUpAssociateNumber = record.associate?.associate_number
      this.showHistoryWriteup = Boolean(this.writeUpAssociateNumber)
    }
  }
}
</script>

<style scoped>
.disciplinary-actions-table__action-link,
.disciplinary-actions-table__person-link {
  white-space: normal;
  text-align: left;
}

.disciplinary-actions-table__reason {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
